import { Component, inject, Inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Store } from '@ngrx/store';
import queryString from 'query-string';
import { BehaviorSubject, of } from 'rxjs';
import { tap, withLatestFrom } from 'rxjs/operators';
import { AuthService } from 'src/app/auth/auth.service';
import { FilterDto, VisualDownloadLinkDto, VisualDownloadLinkType, VisualService } from 'src/app/services/api.services';
import { SnackService } from 'src/app/services/snack.service';
import { getInsightsPbiChartType } from 'src/app/shared/chart/chart';
import { AppSelectors } from 'src/app/state/app';
import { catchAppError, combineForLoading } from '../../utils';
import { VisualApiExportCopiedSnack, VisualApiExportCopiedSnackData } from './copied.snack';
import { AppContext } from 'src/app/app/context';
import { AppCurrencyService } from 'src/app/app/AppCurrencyService';

@Component({
  selector: 'app-visual-api-export-dialog',
  templateUrl: './visual-api-export.dialog.html',
  standalone: false
})
export class VisualApiExportDialog {
  readonly appContext = inject(AppContext);
  readonly currencyService = inject(AppCurrencyService);

  static readonly defaultOptions = {
    width: '800px',
    height: 'auto',
    hasBackdrop: true,
    panelClass: 'dialog-no-padding',
  };

  loadingMetadata$ = new BehaviorSubject<boolean>(false);
  loadingLinks$ = new BehaviorSubject<boolean>(false);
  loading$ = combineForLoading(this.loadingMetadata$, this.loadingLinks$);

  links: VisualDownloadLink[] = [];
  error: string = null;

  metadata: VisualApiExportVisualMetadata = null;
  showCurrency = false;
  showOpenLinks = false;

  refreshDataSet = false;

  currency = '';

  constructor(
    private store: Store,
    public dialogRef: MatDialogRef<VisualApiExportDialog>,
    @Inject(MAT_DIALOG_DATA) public data: VisualApiExportDialogData,
    private authService: AuthService,
    private visualService: VisualService,
    private snackService: SnackService) {
  }

  ngOnInit() {
    this.showCurrency = this.data.showCurrency;
    this.showOpenLinks = this.data.showOpenLinks;

    if (!this.data.metadata) {
      this.loadMetadata();
    }
    else {
      this.metadata = this.data.metadata;
      this.assignCurrency();
    }

    this.loadDownloadLinks();
  }

  assignCurrency() {
    this.loadingMetadata$.next(true);

    this.currencyService.ensureLoaded();

    this.currencyService.currencies$.pipe(
      tap((currencies) => {
        let currencyCode = this.metadata.currency;
        let currencyName = '';
     
        if (!currencyCode || currencyCode == 'Target') {
          currencyCode = currencies.baseCurrencyCode;
          currencyName = currencies.baseCurrency?.currencyName ?? '';
        }
        else {
          currencyName = currencies.get(currencyCode)?.currencyName;
        }

        this.metadata = {
          ...this.metadata,
          currency: currencyName
        };
      }),
      catchAppError(err => {
        this.error = err.message;

        return of(err);
      })
    ).subscribe(() => {
      this.loadingMetadata$.next(false);
    });
  }

  loadMetadata() {
    this.loadingMetadata$.next(true);

    this.currencyService.ensureLoaded();

    this.visualService.getVisual(this.data.visualId, false).pipe(
      withLatestFrom(this.currencyService.currencies$),
      tap(([v, currencies]) => {
        let currencyCode = v.preferences?.currency;
        let currencyName = '';
     
        if (!currencyCode || currencyCode == 'Target') {
          currencyCode = currencies.baseCurrencyCode;
          currencyName = currencies.baseCurrency?.currencyName ?? '';
        }
        else {
          currencyName = currencies.get(currencyCode)?.currencyName;
        }

        this.metadata = {
          name: v.name,
          dataset: v.schema.name,
          isExtendedDataset: !!v.userDatasetId,
          type: getInsightsPbiChartType(v.content.type).title,
          currency: currencyName
        };

        //TODO:this.showCurrency = schema.supportsCurrency;
      }),
      catchAppError(err => {
        this.error = err.message;

        return of(err);
      })
    ).subscribe(() => {
      this.loadingMetadata$.next(false);
    });
  }

  loadDownloadLinks() {
    this.loadingLinks$.next(true);

    this.visualService.getDownloadLinks(this.data.visualId).pipe(
      tap(links => {
        this.links = links.map(l => ({
          ...l,
          urlWithToken: this.createOpenLink(l.url)
        }));
      }),
      catchAppError(err => {
        this.error = err.message;

        return of(err);
      })
    ).subscribe(() => {
      this.loadingLinks$.next(false);
    });
  }

  getLinkTitle(link: VisualDownloadLink) {
    if (link.type == VisualDownloadLinkType.Csv)
      return 'CSV';

    if (link.type == VisualDownloadLinkType.Json)
      return 'JSON';

    if (link.type == VisualDownloadLinkType.TableauWDC)
      return 'Tableau WDC';

    return `Unknown type '${link.type}'`;
  }

  async copyToClipboard(link: VisualDownloadLink) {
    await navigator.clipboard.writeText(this.getLinkUrl(link));

    this.snackService.openFromComponent(VisualApiExportCopiedSnack, {
      verticalPosition: 'top',
      panelClass: ['trg-snack-white', 'min-w-0'],
      duration: 6000,
      data: <VisualApiExportCopiedSnackData>{
        format: this.getLinkTitle(link)
      }
    });
  }

  private createOpenLink(link: string) {
    let url = queryString.parseUrl(link);

    url.query['access_token'] = this.authService.accessToken;
    url.query['inline'] = 'true';

    return queryString.stringifyUrl(url);
  }

  getLinkUrl(link: VisualDownloadLink) {
    if (!this.refreshDataSet)
      return link.url;

    let url = queryString.parseUrl(link.url);

    url.query['forceRefreshCache'] = "1";

    return queryString.stringifyUrl(url);
  }
}

export type VisualDownloadLink = VisualDownloadLinkDto & {
  urlWithToken?: string;
};

interface VisualApiExportVisualMetadata {
  name: string;
  dataset: string;
  isExtendedDataset: boolean;
  type: string;
  currency: string;
}

export interface VisualApiExportDialogData {
  visualId: number;
  metadata?: VisualApiExportVisualMetadata,
  showCurrency?: boolean;
  showOpenLinks?: boolean;
}
